if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection?.querySelector('.product-info');
      this.stickyButton = this.querySelector('[data-sticky-atc-submit]');
      this.label = this.querySelector('[data-sticky-atc-label]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.quickAddDrawer = document.querySelector('quick-add-drawer');
      this.hasVariants = this.dataset.hasVariants === 'true';
      this.successTimer = null;
      this.observedNativeButton = null;

      this.refreshFormReferences();

      if (!this.form || !this.nativeButton || !this.stickyButton) {
        this.hidden = true;
        return;
      }

      this.bindEvents();
      this.syncButton();
      this.updateDrawerState();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      if (this.formObserver) this.formObserver.disconnect();
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.quickAddObserver) this.quickAddObserver.disconnect();
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.successTimer) window.clearTimeout(this.successTimer);
    }

    refreshFormReferences() {
      this.productFormElement = this.productSection?.querySelector('product-form');
      this.form = this.productFormElement?.querySelector('.js-product-form-main');
      this.nativeButton = this.form?.querySelector('[name="add"]');
      this.variantPicker = this.productSection?.querySelector(
        'variant-picker, [data-variant-picker], .product-options'
      );

      this.observeCurrentNativeButton();
    }

    observeCurrentNativeButton() {
      if (!this.nativeButton || this.nativeButton === this.observedNativeButton) return;

      if (this.formObserver) this.formObserver.disconnect();

      this.formObserver = new MutationObserver(() => {
        this.refreshFormReferences();
        this.syncButton();
      });

      this.formObserver.observe(this.nativeButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class', 'data-add-to-cart-text']
      });

      this.observedNativeButton = this.nativeButton;
    }

    bindEvents() {
      this.stickyButton.addEventListener('click', () => this.handleStickyClick());

      this.productSection.addEventListener('on:cart:add', () => this.showSuccess());
      this.productSection.addEventListener('on:line-item:change', () => this.showSuccess());
      this.productSection.addEventListener('on:cart:error', () => this.syncButton());

      // Product forms and variant pickers can be replaced after a selection.
      // Listen at the stable Product section level and reacquire the live form.
      ['click', 'change', 'input'].forEach((eventName) => {
        this.productSection.addEventListener(eventName, (event) => {
          if (
            event.target.closest(
              'variant-picker, [data-variant-picker], .product-options, [name="id"], [data-option-value]'
            )
          ) {
            window.requestAnimationFrame(() => {
              this.refreshFormReferences();
              this.syncButton();
            });
          }
        });
      });

      if (this.cartDrawer) {
        this.drawerObserver = new MutationObserver(() => this.updateDrawerState());
        this.drawerObserver.observe(this.cartDrawer, {
          attributes: true,
          attributeFilter: ['open', 'aria-hidden']
        });
      }

      if (this.quickAddDrawer) {
        this.quickAddObserver = new MutationObserver(() => this.updateDrawerState());
        this.quickAddObserver.observe(this.quickAddDrawer, {
          attributes: true,
          attributeFilter: ['open', 'aria-hidden', 'class']
        });
      }

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (theme.mediaMatches.md && entry.target === this.productInfo) {
              this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
            }
          });
        });

        if (this.productInfo) this.visibilityObserver.observe(this.productInfo);
      }

      this.handleScroll = StickyAtcPanel.handleScroll;
      window.addEventListener('scroll', this.handleScroll, { passive: true });
    }

    updateDrawerState() {
      if (!this.cartDrawer || !this.cartDrawer.isConnected) {
        this.cartDrawer = document.querySelector('cart-drawer');
      }

      if (!this.quickAddDrawer || !this.quickAddDrawer.isConnected) {
        this.quickAddDrawer = document.querySelector('quick-add-drawer');
      }

      const cartOpen = this.cartDrawer
        && (
          this.cartDrawer.hasAttribute('open')
          || this.cartDrawer.getAttribute('aria-hidden') === 'false'
        );

      const quickAddOpen = this.quickAddDrawer
        && (
          this.quickAddDrawer.hasAttribute('open')
          || this.quickAddDrawer.getAttribute('aria-hidden') === 'false'
          || this.quickAddDrawer.classList.contains('is-open')
          || this.quickAddDrawer.classList.contains('is-visible')
        );

      this.classList.toggle(
        'sticky-atc-panel--drawer-open',
        Boolean(cartOpen || quickAddOpen)
      );
    }

    handleStickyClick() {
      this.refreshFormReferences();
      this.syncButton();

      const isMobile = !theme.mediaMatches.md;
      const isUnavailable = this.nativeButton.disabled
        || this.nativeButton.getAttribute('aria-disabled') === 'true';

      const needsSelection = this.nativeButton.dataset.preselectionDisabled === 'true'
        && this.nativeButton.textContent.trim() === this.nativeButton.dataset.preselectionText;

      if (isMobile && this.hasVariants && !this.isVariantPickerVisible()) {
        this.scrollToOptions();
        return;
      }

      if (needsSelection) {
        this.scrollToOptions();
        return;
      }

      if (isUnavailable) return;

      this.nativeButton.click();
      this.syncButton();
    }

    isVariantPickerVisible() {
      this.refreshFormReferences();

      const target = this.variantPicker || this.productFormElement;
      if (!target) return false;

      const rect = target.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const topBoundary = Math.max(0, Math.min(viewportHeight, 120));
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, topBoundary);

      return visibleHeight >= Math.min(80, Math.max(40, rect.height * 0.25));
    }

    scrollToOptions() {
      this.refreshFormReferences();

      const target = this.variantPicker || this.productFormElement;
      if (!target) return;

      const headerOffset = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--header-end'),
        10
      ) || 100;

      const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 20;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });

      target.classList.add('qls-sticky-atc-focus');
      window.setTimeout(() => target.classList.remove('qls-sticky-atc-focus'), 900);
    }

    syncButton() {
      this.refreshFormReferences();

      if (!this.nativeButton || !this.stickyButton || this.stickyButton.classList.contains('is-success')) return;

      const nativeText = this.nativeButton.textContent.trim()
        || this.nativeButton.dataset.addToCartText
        || this.stickyButton.dataset.defaultText
        || 'Add to cart';

      this.label.textContent = nativeText;

      const disabled = this.nativeButton.disabled
        || this.nativeButton.getAttribute('aria-disabled') === 'true';

      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', disabled ? 'true' : 'false');
      this.stickyButton.classList.toggle('is-loading', this.nativeButton.classList.contains('is-loading'));
    }

    showSuccess() {
      if (!this.stickyButton || !this.label) return;

      if (this.successTimer) window.clearTimeout(this.successTimer);

      const previousText = this.label.textContent;
      this.label.textContent = this.dataset.successText || 'Added to cart';
      this.stickyButton.classList.remove('is-loading');
      this.stickyButton.classList.add('is-success');
      this.stickyButton.removeAttribute('aria-disabled');
      this.stickyButton.disabled = false;

      this.successTimer = window.setTimeout(() => {
        this.stickyButton.classList.remove('is-success');
        this.label.textContent = previousText;
        this.syncButton();
      }, 1300);
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
